import clsx from 'clsx';
import { Switch } from '../../catalyst/switch';
import {
  PREVIEW_SIZE_MAX,
  PREVIEW_SIZE_MIN,
  type PagePreviewControlsState,
  type PreviewLayout,
} from './usePagePreviewControls';

const labelClass = 'text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400';

function LayoutSegmented({ value, onChange }: { value: PreviewLayout; onChange: (layout: PreviewLayout) => void }) {
  const option = (layout: PreviewLayout, text: string) => (
    <button
      type="button"
      onClick={() => onChange(layout)}
      className={clsx(
        'px-3 py-1 text-sm font-medium transition-colors',
        value === layout
          ? 'bg-indigo-600 text-white'
          : 'text-gray-600 hover:bg-gray-50 dark:text-zinc-300 dark:hover:bg-zinc-700',
      )}
    >
      {text}
    </button>
  );

  return (
    <div className="inline-flex overflow-hidden rounded-lg border border-gray-200 dark:border-zinc-700">
      {option('flow', 'Flow')}
      {option('stacked', 'Stacked')}
    </div>
  );
}

// Toolbar for the page-previews panel: thumbnail size, compact density,
// per-cluster tint, and the flow/stacked layout switch.
export function PagePreviewControls({
  size,
  setSize,
  compact,
  setCompact,
  tint,
  setTint,
  layout,
  setLayout,
}: PagePreviewControlsState) {
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
      <div className="flex items-center gap-2">
        <span className={labelClass}>Size</span>
        <input
          type="range"
          min={PREVIEW_SIZE_MIN}
          max={PREVIEW_SIZE_MAX}
          value={size}
          onChange={(event) => setSize(Number(event.target.value))}
          className="w-32 accent-indigo-600 dark:accent-indigo-400"
          aria-label="Preview size"
        />
        <span className="w-10 text-xs tabular-nums text-gray-600 dark:text-zinc-300">{size}px</span>
      </div>

      <LayoutSegmented value={layout} onChange={setLayout} />

      <div className="flex items-center gap-2">
        <span className={labelClass}>Compact</span>
        <Switch color="indigo" checked={compact} onChange={setCompact} aria-label="Compact mode" />
      </div>

      <div className="flex items-center gap-2">
        <span className={labelClass}>Tint</span>
        <Switch color="indigo" checked={tint} onChange={setTint} aria-label="Cluster tint" />
      </div>
    </div>
  );
}
